import React, { FC, useEffect, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HTMLMotionProps } from 'framer-motion'

interface Props extends HTMLMotionProps<'div'> {
  courseTitle: string
  currentMMR: number
}

// Component for individual digit animation with scoreboard effect
const AnimatedDigit: FC<{
  digit: string
  index: number
  isAnimating: boolean
}> = ({ digit, index, isAnimating }) => {
  return (
    <div
      className={`relative overflow-hidden h-7 flex items-center justify-center ${
        digit === ',' || digit === '.' ? 'w-1' : 'w-3'
      }`}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={`${digit}-${index}-${isAnimating ? 'new' : 'static'}`}
          initial={
            isAnimating
              ? {
                  y: -28,
                  opacity: 0,
                  rotateX: -90,
                }
              : false
          }
          animate={{
            y: 0,
            opacity: 1,
            rotateX: 0,
          }}
          exit={{
            y: 28,
            opacity: 0,
            rotateX: 90,
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
            mass: 0.6,
            delay: index * 0.03, // Faster stagger for smoother effect
            duration: 0.5,
          }}
          className="absolute inset-0 flex items-center justify-center text-lg font-bold text-blue-600"
          style={{
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
          }}
        >
          {digit}
        </motion.span>
      </AnimatePresence>
    </div>
  )
}

export const AiAssistantCourseMMRDisplay: FC<Props> = ({
  courseTitle,
  currentMMR,
  className = 'sticky top-0 z-10 bg-white bg-gradient-to-r from-blue-50 to-v8-blue/5 border border-blue-200 rounded-lg p-2 md:p-3 mb-2 md:mb-4 shadow-sm',
  ...props
}) => {
  const [displayMMR, setDisplayMMR] = useState(currentMMR)
  const [isAnimating, setIsAnimating] = useState(false)
  const [previousMMR, setPreviousMMR] = useState(currentMMR)

  // Convert number to individual digits for animation
  const formatMMRDigits = useCallback((mmr: number) => {
    return mmr.toLocaleString().split('')
  }, [])

  // Animate MMR changes with scoreboard-style animation
  useEffect(() => {
    if (currentMMR !== displayMMR && currentMMR !== previousMMR) {
      setIsAnimating(true)
      setPreviousMMR(displayMMR)

      // Short delay to allow the animation to start, then update the display value
      const animationTimer = setTimeout(() => {
        setDisplayMMR(currentMMR)
      }, 100)

      // Reset animation state after animation completes
      const resetTimer = setTimeout(() => {
        setIsAnimating(false)
        setPreviousMMR(currentMMR)
      }, 800)

      return () => {
        clearTimeout(animationTimer)
        clearTimeout(resetTimer)
      }
    }
  }, [currentMMR, displayMMR, previousMMR])

  const digits = formatMMRDigits(displayMMR)

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
      {...props}
    >
      {/* Narrow viewports: the row wraps, the title truncates instead of
          crushing the ExamScore pill (Mike, 21 Sep: cramped practice chat). */}
      <div className="flex items-center justify-between gap-x-2 gap-y-1 flex-wrap">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {/* Course Icon */}
          <div className="w-8 h-8 shrink-0 bg-v8-blue/10 rounded-full flex items-center justify-center">
            <span className="text-v8-blue text-sm font-semibold">📚</span>
          </div>

          {/* Course Title */}
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-v8-ink truncate">
              {courseTitle}
            </h3>
            <p className="text-xs text-v8-mut">Practice Session</p>
          </div>
        </div>

        {/* Visely Score Display with Pulse and Scoreboard Animation */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <span className="text-xs md:text-sm text-v8-mut">ExamScore:</span>
          <motion.div
            animate={
              isAnimating
                ? {
                    scale: [1, 1.25, 1],
                    boxShadow: [
                      '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      '0 4px 20px 0 rgba(79, 70, 229, 0.3)',
                      '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    ],
                  }
                : {
                    scale: 1,
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  }
            }
            transition={{
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1], // Using platform's smooth easing
              times: [0, 0.35, 1], // Quick scale up, elegant scale down
            }}
            className="bg-white px-3 py-1 rounded-full border border-blue-200 flex items-center min-h-[32px]"
          >
            {/* Scoreboard-style digit animation */}
            <div className="inline-flex items-center justify-start">
              {digits.map((digit, index) => (
                <AnimatedDigit
                  key={`${index}-${displayMMR}`}
                  digit={digit}
                  index={index}
                  isAnimating={isAnimating}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}

export default AiAssistantCourseMMRDisplay
